/**
 * actionDecidedEvent — the ONE emitter for `prest0n:action-decided`
 * (2026-09-17).
 *
 * Two surfaces now decide a held send: the injected-html card's delegated click
 * handler, and the React staged-send tile. Both have to announce the decision
 * the same way, because the chat listens for it (GlobalChatSystem) to settle
 * the turn's own state. A second hand-rolled `new CustomEvent(...)` is how the
 * two start carrying different details, so there is exactly one.
 *
 * Deliberately a leaf with no imports: it is reached from a React component and
 * from the globalTools hook, and neither should grow an import graph for an
 * event name.
 */

export const ACTION_DECIDED_EVENT = 'prest0n:action-decided';

/**
 * EXACTLY the shape `GlobalChatSystem`'s listener reads. It is richer than it
 * looks and every field earns its place:
 *  - `success` is NOT `ok`. A provider refusal arrives as a perfectly
 *    successful HTTP round trip, so a send is successful only when it was
 *    DELIVERED; a cancel is successful when the decision itself landed.
 *  - `decidedAt` makes each DECISION its own event. A re-armed draft can be
 *    authorized more than once and the listener dedupes by action, so without
 *    it the second attempt's failure is swallowed as a duplicate of the first.
 */
export interface ActionDecidedDetail {
  actionId: string;
  decision: 'authorize' | 'cancel';
  success: boolean;
  delivered: boolean;
  outcome?: string;
  status?: string;
  decidedAt?: string;
  error?: string;
  to: string;
  subject: string;
}

/** The outcome fields this emitter reads — a structural subset of
 *  `PendingActionOutcome`, so the module stays import-free. */
export interface DecidedOutcomeLike {
  decision: 'authorize' | 'cancel';
  ok: boolean;
  delivered?: boolean;
  kind?: string;
  status?: string;
  decidedAt?: string;
  reason?: string | null;
  remediation?: string | null;
  to?: string;
  subject?: string;
}

/**
 * Build the detail from a decision outcome. Exported separately from the
 * dispatch so both the derivation and the wire shape are testable without a
 * DOM, and so neither lane can re-derive `success` its own way.
 */
export function actionDecidedDetail(
  actionId: string,
  outcome: DecidedOutcomeLike,
  fallback?: { to?: string; subject?: string },
): ActionDecidedDetail {
  return {
    actionId,
    decision: outcome.decision,
    success: outcome.decision === 'cancel' ? outcome.ok : outcome.delivered === true,
    delivered: outcome.delivered === true,
    outcome: outcome.kind,
    status: outcome.status,
    decidedAt: outcome.decidedAt,
    error: [outcome.reason, outcome.remediation].filter(Boolean).join(' ') || undefined,
    to: outcome.to || fallback?.to || '',
    subject: outcome.subject || fallback?.subject || '',
  };
}

/** Announce a decided held action. Never throws: a surface that cannot reach
 *  `document` (a test renderer, SSR) still completes its decision. */
export function emitActionDecided(detail: ActionDecidedDetail): void {
  try {
    document.dispatchEvent(new CustomEvent(ACTION_DECIDED_EVENT, { detail }));
  } catch {
    /* the decision already happened server-side; the announcement is a courtesy */
  }
}
